import { ChevronLeft, ChevronRight, Sparkles, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { GLOBAL_STEPS } from '@/libs/constants'
import type { TourStep } from '@/libs/interfaces'
import { cn } from '@/libs/utils'

// ── Global steps (shown on every page) ──

// ── Page-specific step registry ──
// Key = route path prefix (e.g. "/booking"). Steps are appended after global steps.
const PAGE_STEPS_REGISTRY: Record<string, TourStep[]> = {}

/**
 * Register page-specific tour steps for a route prefix.
 * Call this at module level (top of a page/component file).
 *
 * Example:
 *   registerPageTourSteps('/booking', [
 *     { target: 'booking-toggle', titleKey: '...', descriptionKey: '...', position: 'bottom' },
 *   ]);
 */
export function registerPageTourSteps(routePrefix: string, steps: TourStep[]) {
  PAGE_STEPS_REGISTRY[routePrefix] = steps
}

function getStepsForRoute(pathname: string): TourStep[] {
  // Find the longest matching prefix
  let matchedSteps: TourStep[] = []
  let longestMatch = ''

  for (const prefix of Object.keys(PAGE_STEPS_REGISTRY)) {
    if (pathname.startsWith(prefix) && prefix.length > longestMatch.length) {
      longestMatch = prefix
      matchedSteps = PAGE_STEPS_REGISTRY[prefix]
    }
  }

  // If a page has its own steps, show only those; otherwise show global steps
  return matchedSteps.length > 0 ? matchedSteps : GLOBAL_STEPS
}

export function ProductTour() {
  const { t } = useTranslation()
  const location = useLocation()
  const [active, setActive] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 })
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const steps = getStepsForRoute(location.pathname)

  // Filter to only steps whose target element is in the DOM
  const [visibleSteps, setVisibleSteps] = useState<TourStep[]>([])
  useEffect(() => {
    const filtered = steps.filter(
      (s) => document.querySelector(`[data-tour="${s.target}"]`) !== null,
    )
    setVisibleSteps(filtered)
  }, [steps, active])

  useEffect(() => {
    if (currentStep >= visibleSteps.length) {
      setCurrentStep(0)
    }
  }, [currentStep, visibleSteps])

  const totalSteps = visibleSteps.length

  // Listen for external trigger
  useEffect(() => {
    const handler = () => {
      setCurrentStep(0)
      setActive(true)
    }
    window.addEventListener('start-product-tour', handler)
    return () => window.removeEventListener('start-product-tour', handler)
  }, [])

  const calculatePosition = useCallback(() => {
    if (!active || totalSteps === 0) return
    const step = visibleSteps[currentStep]
    if (!step) return
    const el = document.querySelector(`[data-tour="${step.target}"]`)
    if (!el) return

    const rect = el.getBoundingClientRect()
    setSpotlightRect(rect)

    requestAnimationFrame(() => {
      const tooltip = tooltipRef.current
      if (!tooltip) return
      const tw = tooltip.offsetWidth
      const th = tooltip.offsetHeight
      const gap = 16

      let top = 0
      let left = 0

      switch (step.position) {
        case 'right':
          top = rect.top + rect.height / 2 - th / 2
          left = rect.right + gap
          break
        case 'left':
          top = rect.top + rect.height / 2 - th / 2
          left = rect.left - tw - gap
          break
        case 'bottom':
          top = rect.bottom + gap
          left = rect.left + rect.width / 2 - tw / 2
          break
        case 'top':
          top = rect.top - th - gap
          left = rect.left + rect.width / 2 - tw / 2
          break
      }

      top = Math.max(8, Math.min(top, window.innerHeight - th - 8))
      left = Math.max(8, Math.min(left, window.innerWidth - tw - 8))

      setTooltipPos({ top, left })
    })
  }, [active, currentStep, totalSteps, visibleSteps])

  useEffect(() => {
    calculatePosition()
    window.addEventListener('resize', calculatePosition)
    return () => window.removeEventListener('resize', calculatePosition)
  }, [calculatePosition])

  const next = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((s) => s + 1)
    } else {
      setActive(false)
    }
  }

  const prev = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1)
  }

  if (!active || totalSteps === 0) return null

  const step = visibleSteps[currentStep] ?? null
  if (!step) return null

  return (
    <div
      className="fixed inset-0 z-[9999]"
      role="dialog"
      aria-modal="true"
      aria-label={t('tour.title')}
    >
      <svg
        className="absolute inset-0 w-full h-full pointer-events-auto"
        onClick={() => setActive(false)}
      >
        <defs>
          <mask id="tour-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {spotlightRect && (
              <rect
                x={spotlightRect.left - 6}
                y={spotlightRect.top - 6}
                width={spotlightRect.width + 12}
                height={spotlightRect.height + 12}
                rx="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.6)"
          mask="url(#tour-spotlight-mask)"
        />
      </svg>

      {spotlightRect && (
        <div
          className="absolute rounded-lg ring-2 ring-primary ring-offset-2 ring-offset-transparent pointer-events-none animate-pulse"
          style={{
            top: spotlightRect.top - 6,
            left: spotlightRect.left - 6,
            width: spotlightRect.width + 12,
            height: spotlightRect.height + 12,
          }}
        />
      )}

      <div
        ref={tooltipRef}
        className={cn(
          'absolute w-80 bg-card border border-border rounded-xl shadow-2xl p-5 z-[10000]',
          'animate-in fade-in-0 slide-in-from-bottom-2 duration-300',
        )}
        style={{ top: tooltipPos.top, left: tooltipPos.left }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="row">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-xs font-medium text-primary uppercase tracking-wider">
              {t('tour.stepOf', { current: currentStep + 1, total: totalSteps })}
            </span>
          </div>
          <button
            onClick={() => setActive(false)}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <h3 className="text-base font-semibold text-foreground mb-1.5">{t(step.titleKey)}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
          {t(step.descriptionKey)}
        </p>

        <div className="flex items-center gap-1.5 mb-4">
          {visibleSteps.map((_, i) => (
            <div
              key={i}
              className={cn(
                'h-1.5 rounded-full transition-all duration-300',
                i === currentStep
                  ? 'w-6 bg-primary'
                  : i < currentStep
                    ? 'w-1.5 bg-primary/50'
                    : 'w-1.5 bg-muted-foreground/20',
              )}
            />
          ))}
        </div>

        <div className="row-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setActive(false)}
            className="text-caption"
          >
            {t('tour.skip')}
          </Button>
          <div className="row">
            {currentStep > 0 && (
              <Button variant="outline" size="sm" onClick={prev} className="btn-step">
                <ChevronLeft className="w-3 h-3" />
                {t('tour.prev')}
              </Button>
            )}
            <Button size="sm" onClick={next} className="btn-step">
              {currentStep < totalSteps - 1 ? (
                <>
                  {t('tour.next')}
                  <ChevronRight className="w-3 h-3" />
                </>
              ) : (
                t('tour.finish')
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

/** Call this to start the product tour */
export function startProductTour() {
  window.dispatchEvent(new Event('start-product-tour'))
}
